import { useTranslation } from "@redotech/redo-i18n";
import { memo, ReactNode } from "react";
import { RedoRadioButton } from "../arbiter-components/radio/redo-radio-button";
import { Flex } from "../flex";
import { SkeletonText } from "../skeleton";
import { Text } from "../text";
import * as styles from "./address-suggestion.module.css";

export interface AddressFields {
  name: string;
  street1: string;
  street2?: string;
  city: string;
  state?: string;
  zip: string;
  country: string;
}

export type AddressSelection = "original" | "suggested";

export type AddressChangedFields = Partial<
  Record<keyof Omit<AddressFields, "name">, boolean>
>;

export function toAddressFields(address: {
  street1: string;
  street2?: string | null;
  city: string;
  state?: string | null;
  zip: string;
  country: string;
}): Omit<AddressFields, "name"> {
  return {
    street1: address.street1,
    street2: address.street2 ?? undefined,
    city: address.city,
    state: address.state ?? undefined,
    zip: address.zip,
    country: address.country,
  };
}

export interface AddressCardContentProps {
  title?: string;
  fields: Omit<AddressFields, "name">;
  changedFields?: AddressChangedFields;
  variant: AddressSelection;
}

export const AddressCardContent = memo(function AddressCardContent({
  title,
  fields,
  changedFields,
  variant,
}: AddressCardContentProps) {
  const className =
    variant === "original"
      ? styles.highlightOriginal
      : styles.highlightSuggested;

  return (
    <Flex dir="column" gap="none">
      {title && (
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          {title}
        </Text>
      )}
      <Text fontSize="sm" textColor="secondary">
        <HighlightSpan
          changed={changedFields?.street1}
          className={className}
          value={fields.street1}
        />
      </Text>
      {fields.street2 && (
        <Text fontSize="sm" textColor="secondary">
          <HighlightSpan
            changed={changedFields?.street2}
            className={className}
            value={fields.street2}
          />
        </Text>
      )}
      <Text fontSize="sm" textColor="secondary">
        <HighlightSpan
          changed={changedFields?.city}
          className={className}
          value={fields.city}
        />
        {fields.state && (
          <>
            {", "}
            <HighlightSpan
              changed={changedFields?.state}
              className={className}
              value={fields.state}
            />
          </>
        )}{" "}
        <HighlightSpan
          changed={changedFields?.zip}
          className={className}
          value={fields.zip}
        />
      </Text>
      <Text fontSize="sm" textColor="secondary">
        <HighlightSpan
          changed={changedFields?.country}
          className={className}
          value={fields.country}
        />
      </Text>
    </Flex>
  );
});

export const HighlightSpan = memo(function HighlightSpan({
  value,
  changed,
  className,
}: {
  value: string;
  changed: boolean | undefined;
  className: string | undefined;
}) {
  if (!changed) {
    return <>{value}</>;
  }
  return <span className={className}>{value}</span>;
});

export const SuggestedAddressContent = memo(function SuggestedAddressContent({
  address,
  changedFields,
  isLoading,
  hasError,
  errorContent,
}: {
  address: Omit<AddressFields, "name"> | undefined;
  changedFields: AddressChangedFields | undefined;
  isLoading: boolean;
  hasError: boolean;
  errorContent?: ReactNode;
}) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Flex dir="column" gap="none">
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          {t("Suggested address")}
        </Text>
        <Flex dir="column" gap="xs">
          <SkeletonText height="17px" length={20} />
          <SkeletonText height="17px" length={24} />
          <SkeletonText height="18px" length={8} />
        </Flex>
      </Flex>
    );
  }

  if (hasError) {
    return (
      <Flex dir="column" gap="none">
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          {t("Suggested address")}
        </Text>
        {errorContent ?? (
          <Text fontSize="sm" textColor="error">
            {t("Address could not be verified.")}
          </Text>
        )}
      </Flex>
    );
  }

  if (!address) {
    return null;
  }

  return (
    <AddressCardContent
      changedFields={changedFields}
      fields={address}
      title={t("Suggested address")}
      variant="suggested"
    />
  );
});

export interface AddressSelectionRadioGroupProps {
  originalFields: Omit<AddressFields, "name">;
  originalTitle?: string;
  suggestedAddress?: Omit<AddressFields, "name">;
  changedFields?: AddressChangedFields;
  selected: AddressSelection;
  onSelect(selection: AddressSelection): void;
  disabled?: boolean;
  fullWidth?: boolean;
  isLoading?: boolean;
  hasError?: boolean;
  errorContent?: ReactNode;
  direction?: "row" | "column";
  uiSelector?: string;
}

export const AddressSelectionRadioGroup = memo(
  function AddressSelectionRadioGroup({
    originalFields,
    originalTitle,
    suggestedAddress,
    changedFields,
    selected,
    onSelect,
    disabled,
    fullWidth,
    isLoading,
    hasError,
    errorContent,
    direction = "column",
    uiSelector,
  }: AddressSelectionRadioGroupProps) {
    const { t } = useTranslation();

    return (
      <Flex dir={direction} gap="lg">
        <RedoRadioButton
          bordered
          disabled={disabled || hasError}
          fullWidth={fullWidth}
          label={
            <AddressCardContent
              changedFields={hasError ? undefined : changedFields}
              fields={originalFields}
              title={originalTitle ?? t("Current")}
              variant="original"
            />
          }
          onSelect={() => onSelect("original")}
          selected={hasError ? false : selected === "original"}
          size="sm"
          uiSelector={uiSelector}
        />
        <RedoRadioButton
          bordered
          disabled={disabled || hasError}
          fullWidth={fullWidth}
          label={
            <SuggestedAddressContent
              address={suggestedAddress}
              changedFields={hasError ? undefined : changedFields}
              errorContent={errorContent}
              hasError={hasError ?? false}
              isLoading={isLoading ?? false}
            />
          }
          onSelect={() => onSelect("suggested")}
          selected={hasError ? false : selected === "suggested"}
          size="sm"
          uiSelector={uiSelector}
        />
      </Flex>
    );
  },
);
